import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Typography, Button } from "antd";
import { UnorderedListOutlined } from "@ant-design/icons";
import {
  Plus,
  Target,
  TrendingUp,
  Zap,
  BarChart3,
  Clock,
  DollarSign,
} from "lucide-react";

const { Title, Paragraph } = Typography;

interface CardData {
  title: string;
  icon: React.ComponentType<any>;
  description: string;
  gradientFrom: string;
  gradientTo: string;
  gradientClass: string;
  iconGradient: string;
  badge?: string;
}

const BidLanding: React.FC = () => {
  const navigate = useNavigate();
  const [animatedCards, setAnimatedCards] = useState<boolean[]>([]);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedCards(new Array(6).fill(true));
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  const cardData: CardData[] = [
    {
      title: "Advance Audience Creation",
      icon: Target,
      description:
        "Build highly targeted audience groups using behavior, demographics, and intent to improve engagement and conversions.",
      gradientFrom: "#3b82f6",
      gradientTo: "#8b5cf6",
      gradientClass: "from-blue-500 to-purple-600",
      iconGradient: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
      badge: "AI Powered",
    },
    {
      title: "Sell-Through Optimization",
      icon: TrendingUp,
      description:
        "Boost sales efficiency by aligning inventory with demand and optimizing promotions to reduce stock issues.",
      gradientFrom: "#10b981",
      gradientTo: "#06b6d4",
      gradientClass: "from-emerald-500 to-cyan-500",
      iconGradient: "linear-gradient(135deg, #10b981, #06b6d4)",
    },
    {
      title: "Leads Quality Optimization",
      icon: Zap,
      description:
        "Enhance lead quality by identifying and prioritizing high-intent prospects for better conversion rates.",
      gradientFrom: "#f59e0b",
      gradientTo: "#ef4444",
      gradientClass: "from-amber-500 to-red-500",
      iconGradient: "linear-gradient(135deg, #f59e0b, #ef4444)",
      badge: "Live",
    },
    {
      title: "Bid Optimizer Activation",
      icon: DollarSign,
      description:
        "Automate bidding with real-time data signals to improve ad performance and maximize return on spend.",
      gradientFrom: "#8b5cf6",
      gradientTo: "#ec4899",
      gradientClass: "from-purple-600 to-pink-500",
      iconGradient: "linear-gradient(135deg, #8b5cf6, #ec4899)",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12 md:px-6 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
        <div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      <div className="relative z-10">
        <div className="text-center mb-12">
          <Title
            level={1}
            className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6"
          >
            Buy Media based-on First-party data
          </Title>
        </div>

        <div className="text-center flex flex-col sm:flex-row justify-center items-center gap-4 mb-12">
          <Button
            onClick={() => navigate("/optimization/bid-optimizer")}
            icon={<UnorderedListOutlined />}
            size="large"
            className="flex items-center gap-2 hover:border-blue-400 hover:text-blue-600 transition-all duration-300 shadow-md hover:shadow-lg"
          >
            View Past Tests
          </Button>
          <Button
            onClick={() => navigate("/jobs/BidOptimizer")}
            type="primary"
            icon={<Plus className="h-4 w-4" />}
            size="large"
            className="bg-gradient-to-r from-blue-600 to-purple-600 border-none hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          >
            Run Bid Optimizer Test
          </Button>
        </div>

        {/* Enhanced Divider */}

        <div className="grid grid-cols-1 md:grid-cols-2 mx-6 xl:grid-cols-2 gap-5">
          {cardData.map((card, index) => {
            const IconComponent = card.icon;
            const isAnimated = animatedCards[index];

            return (
              <Card
                key={index}
                className={`
                  shadow-lg hover:shadow-2xl rounded-2xl border border-gray-200 
                  relative group overflow-hidden transition-all duration-700 
                  transform hover:scale-105 hover:-translate-y-2
                  ${
                    isAnimated
                      ? "animate-in slide-in-from-bottom-4 fade-in"
                      : "opacity-0"
                  }
                `}
                style={{
                  animationDelay: `${index * 150}ms`,
                  animationDuration: "800ms",
                  animationFillMode: "forwards",
                }}
                bodyStyle={{ padding: "24px" }}
              >
                {/* Enhanced background effects */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(135deg, ${card.gradientFrom}08, ${card.gradientTo}08)`,
                    }}
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `radial-gradient(circle at 20% 80%, ${card.gradientFrom}15 0%, transparent 50%), radial-gradient(circle at 80% 20%, ${card.gradientTo}15 0%, transparent 50%)`,
                    }}
                  />
                </div>

                {/* Animated top accent */}
                <div
                  className="absolute top-0 left-0 right-0 h-1 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700 ease-out"
                  style={{ background: card.iconGradient }}
                />
                {/* Header with icon and title */}
                <div className="flex items-center gap-4 mb-4 relative z-10">
                  <div
                    className="p-3 rounded-xl transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 shadow-md"
                    style={{
                      background: card.iconGradient,
                      boxShadow: `0 4px 20px ${card.gradientFrom}40`,
                    }}
                  >
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>

                  <Title
                    level={4}
                    className="text-gray-800 font-semibold mb-0 group-hover:text-gray-900 transition-colors duration-300"
                  >
                    {card.title}
                  </Title>
                </div>

                {/* Description */}
                <div className="relative z-10">
                  <Paragraph className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                    {card.description}
                  </Paragraph>
                </div>

                {/* Hover indicator */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
                  <div
                    className="w-2 h-2 rounded-full animate-pulse"
                    style={{ backgroundColor: card.gradientFrom }}
                  />
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BidLanding;
